import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Unlock, Award, ArrowRight, Pencil, Sparkles, TrendingUp, StickyNote } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

const CAT_META: Record<string, { icon: any; cls: string }> = {
  NOTE: { icon: StickyNote, cls: "bg-zinc-50 text-zinc-700 border-zinc-200" },
  STRENGTH: { icon: Sparkles, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  DEVELOPMENT: { icon: TrendingUp, cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

export default function TrainerDashboard() {
  const { t, i18n } = useTranslation();
  const fr = i18n.language === "fr";
  const [learners, setLearners] = useState<any[]>([]);
  const [coaching, setCoaching] = useState<any>(null);
  const [attempts, setAttempts] = useState<any[] | null>(null);
  const [unlockTarget, setUnlockTarget] = useState<any>(null);
  const [reason, setReason] = useState("");
  const [noteText, setNoteText] = useState("");
  const [noteCat, setNoteCat] = useState("NOTE");
  const [editNote, setEditNote] = useState<any>(null);

  const load = () => api.get("/trainer/learners").then((r) => setLearners(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const title = (o: any) => (fr ? o?.title_fr : o?.title_en) || "—";
  const fmtDate = (s?: string) => (s ? s.slice(0, 16).replace("T", " ") : "—");

  const openCoaching = async (l: any) => {
    setAttempts(null);
    try {
      const { data } = await api.get(`/trainer/learners/${l.id}/coaching`);
      setCoaching(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const viewAttempts = async (l: any) => {
    try {
      const { data } = await api.get(`/trainer/learners/${l.id}/attempts`);
      setAttempts(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const addNote = async () => {
    try {
      await api.post(`/trainer/learners/${coaching.learner.id}/notes`, { category: noteCat, text: noteText });
      toast.success(t("trainer.noteAdded"));
      setNoteText("");
      openCoaching(coaching.learner);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveNote = async () => {
    try {
      await api.put(`/trainer/notes/${editNote.id}`, { text: editNote.text });
      toast.success(t("trainer.noteUpdated"));
      setEditNote(null);
      openCoaching(coaching.learner);
    } catch (e) { toast.error(apiError(e)); }
  };

  const doUnlock = async () => {
    try {
      await api.post("/trainer/unlock", { learner_id: unlockTarget.learner_id, course_id: unlockTarget.course_id, reason });
      toast.success(t("trainer.unlocked"));
      setUnlockTarget(null); setReason("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="trainer-dashboard">
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight fade-up">{t("trainer.title")}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 fade-up-1">

        {/* ---- Assigned learners ---- */}
        <div className="lg:col-span-2 swiss-card p-6" data-testid="trainer-learner-list">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("trainer.learners")}</p>
          <div className="mt-4 space-y-3">
            {learners.length === 0 && <p className="text-sm text-muted-foreground">{t("trainer.noLearners")}</p>}
            {learners.map((l) => (
              <div key={l.id} className={`border rounded-md p-4 transition-colors duration-200 ${coaching?.learner?.id === l.id ? "border-[#0A1F44] bg-[#0A1F44]/[0.03]" : "border-border"}`} data-testid={`trainer-learner-${l.email}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="font-medium text-sm">{l.name}</p>
                    <p className="text-xs text-muted-foreground">{l.email}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      <span className="font-mono-v">{l.system_role}</span> · <span className="font-mono-v">{l.career_stage}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("trainer.lastActivity")}</p>
                    <p className="text-xs font-mono-v" data-testid={`last-activity-${l.email}`}>{fmtDate(l.last_activity)}</p>
                    <p className="text-xs mt-1 flex items-center justify-end gap-1"><Award size={12} className="text-[#C8A44D]" /> {(l.certifications || []).filter((c: any) => c.status === "PASSED").length} {t("trainer.certifications").toLowerCase()}</p>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {(l.enrollments || []).map((e: any) => (
                    <div key={e.id} className="text-xs">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground truncate">{title(e)}</span>
                        <span className="font-mono-v">{e.progress_pct}%</span>
                      </div>
                      <div className="h-1 bg-zinc-100 rounded-full mt-0.5"><div className="h-1 bg-[#0A1F44] rounded-full" style={{ width: `${e.progress_pct}%` }} /></div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={() => openCoaching(l)} data-testid={`coaching-btn-${l.email}`}
                    className="text-xs font-medium bg-[#0A1F44] text-white px-3 py-1.5 rounded-md hover:bg-[#132c5c] transition-colors duration-200">
                    {t("trainer.coaching")}
                  </button>
                  <button onClick={() => { if (coaching?.learner?.id !== l.id) openCoaching(l); viewAttempts(l); }} data-testid={`view-attempts-${l.email}`}
                    className="text-xs font-medium text-[#002BF6] border border-[#002BF6] px-3 py-1.5 rounded-md hover:bg-[#002BF6]/5 transition-colors duration-200">
                    {t("trainer.attempts")}
                  </button>
                </div>
                {(l.review_required || []).length > 0 && (
                  <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3" data-testid={`review-required-${l.email}`}>
                    <p className="text-xs font-semibold text-[#FF3B30] flex items-center gap-1.5">
                      <AlertTriangle size={13} /> {t("trainer.reviewRequired")}
                    </p>
                    {l.review_required.map((s: any) => (
                      <button key={s.id} onClick={() => setUnlockTarget({ learner_id: l.id, course_id: s.course_id })}
                        data-testid={`unlock-btn-${l.email}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium bg-[#FF3B30] text-white px-3 py-1.5 rounded-md hover:bg-[#e0342a] transition-colors duration-200">
                        <Unlock size={12} /> {t("trainer.unlock")}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ---- Coaching view ---- */}
        <div className="lg:col-span-3 space-y-6">
          {!coaching && (
            <div className="swiss-card p-6" data-testid="coaching-empty">
              <p className="text-sm text-muted-foreground">{t("trainer.selectLearner")}</p>
            </div>
          )}
          {coaching && (
            <div className="swiss-card p-6" data-testid="coaching-view">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{t("trainer.coachingView")}</p>
              <div className="mt-3 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="font-heading text-xl font-semibold">{coaching.learner.name}</h2>
                  <p className="text-xs text-muted-foreground">{coaching.learner.email} · <span className="font-mono-v">{coaching.learner.system_role}</span> · <span className="font-mono-v">{coaching.learner.career_stage}</span></p>
                </div>
              </div>

              {/* Next recommended action */}
              <div className="mt-4 border border-[#C8A44D]/40 bg-[#C8A44D]/5 rounded-md p-4" data-testid="coaching-next-action">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#8a6d2b] flex items-center gap-1.5"><ArrowRight size={13} /> {t("trainer.nextAction")}</p>
                {coaching.next_action ? (
                  <p className="text-sm mt-1.5">
                    {coaching.next_action.type === "CONTINUE_COURSE" ? t("trainer.continueCourse") : t("trainer.enrollCourse")}{" "}
                    <span className="font-medium">{title(coaching.next_action)}</span>
                    {coaching.next_action.progress_pct !== undefined && <span className="font-mono-v text-muted-foreground"> — {coaching.next_action.progress_pct}%</span>}
                  </p>
                ) : <p className="text-sm mt-1.5 text-muted-foreground">{t("trainer.noNextAction")}</p>}
              </div>

              {/* Progress */}
              <div className="mt-5">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{t("trainer.completion")}</p>
                <div className="mt-2 space-y-2" data-testid="coaching-progress">
                  {coaching.progress.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
                  {coaching.progress.map((e: any) => (
                    <div key={e.id} className="text-sm">
                      <div className="flex justify-between"><span>{title(e)}</span><span className="font-mono-v">{e.progress_pct}%</span></div>
                      <div className="h-1.5 bg-zinc-100 rounded-full mt-1"><div className="h-1.5 bg-[#0A1F44] rounded-full" style={{ width: `${e.progress_pct}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Certification history */}
              <div className="mt-5">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{t("trainer.certHistory")}</p>
                <div className="mt-2 space-y-1.5" data-testid="coaching-certs">
                  {coaching.certifications.length === 0 && <p className="text-sm text-muted-foreground">{t("trainer.noCerts")}</p>}
                  {coaching.certifications.map((c: any) => (
                    <div key={c.id} className="flex items-center justify-between text-sm border border-border rounded-md px-3 py-2">
                      <span className="flex items-center gap-2"><Award size={14} className="text-[#C8A44D]" /> {title(c)}</span>
                      <span className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground font-mono-v">{fmtDate(c.issued_at)}</span>
                        <span className={`px-2 py-0.5 rounded font-mono-v font-semibold ${c.status === "PASSED" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{c.status}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Private coaching notes */}
              <div className="mt-5">
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{t("trainer.notes")}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.keys(CAT_META).map((c) => (
                    <button key={c} onClick={() => setNoteCat(c)} data-testid={`note-cat-${c}`}
                      className={`text-xs px-3 py-1.5 rounded-md border transition-colors duration-200 ${noteCat === c ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "border-border text-muted-foreground hover:bg-muted"}`}>
                      {t(`trainer.noteCategories.${c}`)}
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)}
                    placeholder={t("trainer.notePlaceholder")}
                    className="flex-1 px-3 py-2 border border-border rounded-md text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" />
                  <button onClick={addNote} disabled={!noteText.trim()} data-testid="note-add-btn"
                    className="px-4 py-2 text-sm bg-[#0A1F44] text-white rounded-md hover:bg-[#132c5c] transition-colors duration-200 disabled:opacity-50">
                    {t("trainer.addNote")}
                  </button>
                </div>
                <div className="mt-3 space-y-2" data-testid="notes-list">
                  {coaching.notes.length === 0 && <p className="text-sm text-muted-foreground">{t("trainer.noNotes")}</p>}
                  {coaching.notes.map((n: any) => {
                    const M = CAT_META[n.category] || CAT_META.NOTE;
                    const Icon = M.icon;
                    return (
                      <div key={n.id} className={`border rounded-md p-3 ${M.cls}`} data-testid={`note-${n.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[10px] font-semibold uppercase tracking-wider flex items-center gap-1.5"><Icon size={12} /> {t(`trainer.noteCategories.${n.category}`)}</span>
                          <span className="flex items-center gap-2 text-[10px] font-mono-v opacity-70">
                            {fmtDate(n.updated_at || n.created_at)}
                            <button onClick={() => setEditNote({ id: n.id, text: n.text })} data-testid={`note-edit-${n.id}`} className="hover:opacity-100 opacity-70"><Pencil size={12} /></button>
                          </span>
                        </div>
                        {editNote?.id === n.id ? (
                          <div className="mt-2 flex gap-2">
                            <input data-testid="note-edit-input" value={editNote.text} onChange={(e) => setEditNote({ ...editNote, text: e.target.value })}
                              className="flex-1 px-2 py-1.5 border border-border rounded-md text-sm bg-white text-foreground" />
                            <button onClick={saveNote} data-testid="note-edit-save" className="px-3 py-1.5 text-xs bg-[#0A1F44] text-white rounded-md">{t("common.save")}</button>
                            <button onClick={() => setEditNote(null)} data-testid="note-edit-cancel" className="px-3 py-1.5 text-xs border border-border rounded-md bg-white text-foreground">{t("common.cancel")}</button>
                          </div>
                        ) : <p className="text-sm mt-1.5">{n.text}</p>}
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground mt-3">{t("trainer.privateNotice")}</p>
              </div>
            </div>
          )}

          {/* Attempts */}
          {attempts !== null && (
            <div className="swiss-card p-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                {t("trainer.attempts")} {coaching ? `— ${coaching.learner.name}` : ""}
              </p>
              <div className="mt-4 space-y-2" data-testid="attempts-list">
                {attempts.map((a) => (
                  <div key={a.id} className="border border-border rounded-md p-3 text-xs flex items-center justify-between">
                    <div>
                      <span className="font-mono-v">#{a.attempt_number}</span>
                      {a.course && <span className="ml-2 font-medium">{a.course.slug}</span>}
                      <span className="text-muted-foreground ml-2">{a.timestamp?.slice(0, 16).replace("T", " ")}</span>
                      <span className="ml-2 text-muted-foreground">v{a.version}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-v font-semibold">{a.score_pct}%</span>
                      <span className={`px-2 py-0.5 rounded font-mono-v font-semibold ${a.passed ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
                        {a.passed ? "PASS" : a.review_state}
                      </span>
                    </div>
                  </div>
                ))}
                {attempts.length === 0 && <p className="text-sm text-muted-foreground">—</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {unlockTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="unlock-modal">
          <div className="bg-white rounded-md p-6 w-full max-w-md">
            <h3 className="font-heading font-semibold text-lg">{t("trainer.unlock")}</h3>
            <label className="block text-sm font-medium mt-4 mb-1.5">{t("trainer.unlockReason")}</label>
            <textarea data-testid="unlock-reason-input" value={reason} onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none" rows={3} />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setUnlockTarget(null)} data-testid="unlock-cancel"
                className="px-4 py-2 text-sm border border-border rounded-md hover:bg-muted transition-colors duration-200">{t("common.cancel")}</button>
              <button onClick={doUnlock} disabled={!reason.trim()} data-testid="unlock-confirm"
                className="px-4 py-2 text-sm bg-[#FF3B30] text-white rounded-md hover:bg-[#e0342a] transition-colors duration-200 disabled:opacity-50">
                {t("trainer.confirmUnlock")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
